import { useState, type FC } from "react";
import { AlertCircle, CheckCircle, DollarSign, FileText, X } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";

import type { LawyerEstateRow } from "@/shared/types/lawyerDashboard";
import { IconButton } from "@/shared/components/ui/icon-button";

interface AlertItem {
  id: string;
  label: string;
  decedentName: string | null;
  timeAgo: string;
  category: "action" | "passive";
}

function getIcon(category: "action" | "passive", label: string) {
  if (category === "action") {
    return <AlertCircle className="h-4 w-4" aria-hidden="true" />;
  }
  if (label.toLowerCase().includes("asset") || label.toLowerCase().includes("debt")) {
    return <DollarSign className="h-4 w-4" aria-hidden="true" />;
  }
  if (label.toLowerCase().includes("document") || label.toLowerCase().includes("uploaded")) {
    return <FileText className="h-4 w-4" aria-hidden="true" />;
  }
  return <CheckCircle className="h-4 w-4" aria-hidden="true" />;
}

interface AlertItemRowProps {
  item: AlertItem;
}

const AlertItemRow: FC<AlertItemRowProps> = ({ item }) => {
  const isAction = item.category === "action";

  return (
    <div className="flex items-start gap-3 rounded-sm p-3 transition-colors hover:bg-subtle">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-primary bg-white text-secondary">
        {getIcon(item.category, item.label)}
      </span>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${isAction ? "text-warning" : "text-primary"}`}>
          {item.label}
        </p>
        <p className="mt-0.5 text-2xs text-secondary">
          {item.decedentName ? `${item.decedentName} estate` : "Unknown estate"} · {item.timeAgo}
        </p>
      </div>
    </div>
  );
};

interface AlertsPanelProps {
  estates: LawyerEstateRow[];
  children: React.ReactNode;
}

export const AlertsPanel: FC<AlertsPanelProps> = ({ estates, children }) => {
  const [open, setOpen] = useState(false);

  const alertItems: AlertItem[] = estates
    .filter((e) => e.highlight !== null)
    .map((e) => ({
      id: e.id,
      label: e.highlight!.label,
      decedentName: e.decedentName,
      timeAgo: e.highlight!.timeAgo,
      category: e.highlight!.category,
    }));

  const actionItems = alertItems.filter((i) => i.category === "action");
  const passiveItems = alertItems.filter((i) => i.category === "passive");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 max-h-[600px] overflow-y-auto p-0 shadow-[0_0_12px_rgba(0,0,0,0.09)]"
      >
        <div className="flex items-center justify-between px-4 pb-1 pt-4">
          <p className="text-sm font-medium">Alerts</p>
          <IconButton onClick={() => setOpen(false)} label="Close alerts" tooltip={false}>
            <X />
          </IconButton>
        </div>

        <div className="p-2">
          {actionItems.length === 0 && passiveItems.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-secondary">No alerts at this time.</p>
            </div>
          ) : (
            <>
              {actionItems.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <span className="size-2 shrink-0 rounded-full bg-warning" aria-hidden="true" />
                    <p className="text-2xs font-semibold text-warning">Action needed</p>
                  </div>
                  {actionItems.map((item) => (
                    <AlertItemRow key={item.id} item={item} />
                  ))}
                </div>
              )}

              {actionItems.length > 0 && passiveItems.length > 0 && (
                <div className="my-1 border-t border-primary" />
              )}

              {passiveItems.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <p className="text-2xs font-semibold text-primary">Updates</p>
                  </div>
                  {passiveItems.map((item) => (
                    <AlertItemRow key={item.id} item={item} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
